/**
 * PluginBridge — Host-side bridge for communicating with plugin iframes.
 *
 * Handles postMessage communication, permission enforcement,
 * rate limiting, and proxying requests to the FIAS backend.
 */

/**
 * Plugin permission scopes (matches @fias/db-types PluginPermission).
 * Redefined here to avoid client dependency on server-only package.
 *
 * SYNC NOTE: Keep in sync with:
 *   - packages/db-types/src/plugin.ts (PluginPermission)
 *   - packages/arche-sdk/src/types.ts (PluginPermission)
 */
type PluginPermission =
  | 'user:profile:read'
  | 'entities:invoke'
  | 'storage:sandbox'
  | 'theme:read'
  | 'data:store';

// Bridge message types (must match the SDK's types)
interface BridgeMessage {
  type: string;
  messageId: string;
  payload: unknown;
}

interface PluginBridgeOptions {
  archId: string;
  permissions: PluginPermission[];
  iframe: HTMLIFrameElement;
  onResize?: (height: number) => void;
  onToast?: (message: string, variant: string) => void;
  onNavigate?: (path: string) => void;
  getTheme: () => unknown;
  getUser: () => Promise<{ userId: string; displayName: string; avatar: string | null }>;
  bridgeApiUrl: string;
  getAuthToken: () => Promise<string>;
  /** When true, uses local mocks for storage and custom handler for entity invoke. */
  previewMode?: boolean;
  /** Custom entity_invoke handler for preview (avoids backend proxy for entity lookup). */
  onEntityInvoke?: (payload: {
    entityId: string;
    input: string;
    systemPrompt?: string;
  }) => Promise<{ output: string }>;
  /** Custom image_generate handler for preview (avoids backend proxy for image config lookup). */
  onImageGenerate?: (payload: {
    entityId: string;
    prompt: string;
    size?: string;
    quality?: string;
    style?: string;
  }) => Promise<{
    imageUrl: string;
    imageRef: string;
    revisedPrompt?: string;
    provider: string;
    model: string;
  }>;
  /** External storage map for preview mode — survives bridge rebuilds when passed from parent. */
  previewStorage?: Map<string, string>;
  /** Custom storage handler for preview mode — when provided, proxies to backend instead of in-memory. */
  onStorage?: (action: string, params: Record<string, unknown>) => Promise<unknown>;
  /** Callback for bridge-level log events (e.g., burst detection warnings). */
  onLog?: (level: 'warn' | 'error', message: string) => void;
}

/**
 * Rate limiting state per message type.
 */
interface RateLimitBucket {
  count: number;
  windowStart: number;
}

const RATE_LIMITS: Record<string, { maxPerMinute: number }> = {
  entity_invoke: { maxPerMinute: 60 },
  image_generate: { maxPerMinute: 10 },
  storage_write: { maxPerMinute: 120 },
  storage_read: { maxPerMinute: 300 },
  storage_list: { maxPerMinute: 60 },
  storage_delete: { maxPerMinute: 60 },
  data_put: { maxPerMinute: 120 },
  data_get: { maxPerMinute: 300 },
  data_query: { maxPerMinute: 60 },
  data_delete: { maxPerMinute: 60 },
  data_create_collection: { maxPerMinute: 10 },
  data_delete_collection: { maxPerMinute: 10 },
  data_list_collections: { maxPerMinute: 60 },
};

/** Burst detection: catches runaway loops (e.g., useEffect calling dataStore.put every render). */
interface BurstBucket {
  count: number;
  windowStart: number;
  /** When set, all calls of this type are blocked until this timestamp. */
  blockedUntil?: number;
}

const BURST_THRESHOLD = 50;
const BURST_WINDOW_MS = 5_000;
const BURST_COOLDOWN_MS = 10_000;

/**
 * Permission requirements per bridge call type.
 */
const PERMISSION_MAP: Record<string, PluginPermission> = {
  get_user: 'user:profile:read',
  get_theme: 'theme:read',
  entity_invoke: 'entities:invoke',
  image_generate: 'entities:invoke',
  storage_read: 'storage:sandbox',
  storage_write: 'storage:sandbox',
  storage_list: 'storage:sandbox',
  storage_delete: 'storage:sandbox',
  data_create_collection: 'data:store',
  data_list_collections: 'data:store',
  data_delete_collection: 'data:store',
  data_put: 'data:store',
  data_get: 'data:store',
  data_query: 'data:store',
  data_delete: 'data:store',
};

export class PluginBridgeHost {
  private options: PluginBridgeOptions;
  private rateLimits = new Map<string, RateLimitBucket>();
  private burstBuckets = new Map<string, BurstBucket>();
  /** In-memory storage for preview mode (no backend calls needed). */
  private previewStorage: Map<string, string>;
  /** In-memory data store for preview mode — maps collection → (key → document). */
  private previewDataStore = new Map<string, Map<string, Record<string, unknown>>>();
  /** Collection metadata for preview data store. */
  private previewCollections = new Map<
    string,
    { name: string; userScope: string; createdAt: string }
  >();

  constructor(options: PluginBridgeOptions) {
    this.options = options;
    this.previewStorage = options.previewStorage ?? new Map<string, string>();
    this.handleMessage = this.handleMessage.bind(this);
    window.addEventListener('message', this.handleMessage);
  }

  /**
   * Send the init message to the plugin iframe to establish the bridge.
   */
  init(): void {
    this.sendToPlugin({
      type: 'init',
      messageId: 'init_0',
      payload: {
        archId: this.options.archId,
        permissions: this.options.permissions,
        theme: this.options.getTheme(),
        currentPath: window.location.pathname,
      },
    });
  }

  /**
   * Notify the plugin of a theme change.
   */
  sendThemeUpdate(): void {
    this.sendToPlugin({
      type: 'theme_update',
      messageId: `theme_${Date.now()}`,
      payload: this.options.getTheme(),
    });
  }

  /**
   * Navigate the plugin to a specific step/node.
   */
  navigateToStep(nodeId: string): void {
    this.sendToPlugin({
      type: 'step_navigate',
      messageId: `step_${Date.now()}`,
      payload: { nodeId },
    });
  }

  /**
   * Clean up event listeners.
   */
  destroy(): void {
    window.removeEventListener('message', this.handleMessage);
  }

  private async handleMessage(event: MessageEvent): Promise<void> {
    // Only handle messages from our plugin iframe
    if (event.source !== this.options.iframe.contentWindow) return;

    const data = event.data as BridgeMessage;
    if (!data || typeof data !== 'object' || !data.type || !data.messageId) return;

    // Fire-and-forget messages (no response needed)
    if (data.type === 'ready') return;

    if (data.type === 'resize') {
      const { height } = data.payload as { height: number };
      if (typeof height === 'number' && height > 0) {
        this.options.onResize?.(height);
      }
      return;
    }

    if (data.type === 'toast') {
      const { message, variant } = data.payload as { message: string; variant: string };
      if (typeof message === 'string') {
        this.options.onToast?.(message, variant ?? 'info');
      }
      return;
    }

    if (data.type === 'navigate') {
      const { path } = data.payload as { path: string };
      if (typeof path === 'string' && path.startsWith('/a/')) {
        this.options.onNavigate?.(path);
      }
      return;
    }

    // Request/response messages — check permissions and rate limits
    try {
      const result = await this.handleRequest(data);
      this.sendResponse(data.messageId, result);
    } catch (err) {
      this.sendError(data.messageId, err instanceof Error ? err.message : 'Unknown error');
    }
  }

  private async handleRequest(msg: BridgeMessage): Promise<unknown> {
    // In preview mode, skip permission checks (all permissions granted)
    if (!this.options.previewMode) {
      const requiredPermission = PERMISSION_MAP[msg.type];
      if (requiredPermission && !this.options.permissions.includes(requiredPermission)) {
        throw new Error(`Permission denied: ${requiredPermission} not granted`);
      }
    }

    // Rate limiting applies in all modes (protects against runaway loops in preview)
    this.checkRateLimit(msg.type);

    switch (msg.type) {
      case 'get_user':
        return this.options.getUser();

      case 'get_theme':
        return this.options.getTheme();

      case 'entity_invoke': {
        const payload = msg.payload as {
          entityId: string;
          input: string;
          stream?: boolean;
          systemPrompt?: string;
        };
        if (this.options.previewMode && this.options.onEntityInvoke) {
          if (payload.stream) {
            return this.handlePreviewStreamingInvoke(msg.messageId, payload);
          }
          return this.options.onEntityInvoke(payload);
        }
        // Published plugins: handle streaming via SSE through the bridge endpoint
        if (payload.stream) {
          return this.handlePublishedStreamingInvoke(msg.messageId, payload);
        }
        return this.proxyToBackend(msg);
      }

      case 'image_generate': {
        const imgPayload = msg.payload as {
          entityId: string;
          prompt: string;
          size?: string;
          quality?: string;
          style?: string;
        };
        if (this.options.previewMode && this.options.onImageGenerate) {
          return this.options.onImageGenerate(imgPayload);
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_read': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('read', { path });
          }
          const { path } = msg.payload as { path?: string };
          const content = this.previewStorage.get(path ?? '') ?? null;
          return { content, exists: content !== null };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_write': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path, content } = msg.payload as { path?: string; content?: string };
            return this.options.onStorage('write', { path, content });
          }
          const { path, content } = msg.payload as { path?: string; content?: string };
          if (path && content !== undefined) {
            this.previewStorage.set(path, content);
          }
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_list': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { prefix } = msg.payload as { prefix?: string };
            return this.options.onStorage('list', { prefix });
          }
          const { prefix } = msg.payload as { prefix?: string };
          const files = [...this.previewStorage.keys()].filter(
            (k) => !prefix || k.startsWith(prefix),
          );
          return { files };
        }
        return this.proxyToBackend(msg);
      }

      case 'storage_delete': {
        if (this.options.previewMode) {
          if (this.options.onStorage) {
            const { path } = msg.payload as { path?: string };
            return this.options.onStorage('delete', { path });
          }
          const { path } = msg.payload as { path?: string };
          if (path) this.previewStorage.delete(path);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      // ── Data Store (in-memory for preview mode) ─────────────────────

      case 'data_create_collection': {
        if (this.options.previewMode) {
          const { name, userScope } = msg.payload as { name: string; userScope?: string };
          if (!this.previewDataStore.has(name)) {
            this.previewDataStore.set(name, new Map());
          }
          const meta = {
            name,
            userScope: userScope ?? 'user',
            createdAt: new Date().toISOString(),
          };
          this.previewCollections.set(name, meta);
          return { collection: meta };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_list_collections': {
        if (this.options.previewMode) {
          return { collections: [...this.previewCollections.values()] };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete_collection': {
        if (this.options.previewMode) {
          const { name } = msg.payload as { name: string };
          this.previewDataStore.delete(name);
          this.previewCollections.delete(name);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_put': {
        if (this.options.previewMode) {
          const { collection, key, data } = msg.payload as {
            collection: string;
            key: string;
            data: Record<string, unknown>;
          };
          let col = this.previewDataStore.get(collection);
          if (!col) {
            // Auto-create collection on first put
            col = new Map();
            this.previewDataStore.set(collection, col);
            this.previewCollections.set(collection, {
              name: collection,
              userScope: 'user',
              createdAt: new Date().toISOString(),
            });
          }
          col.set(key, { ...data, _key: key, _updatedAt: new Date().toISOString() });
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_get': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          const doc = this.previewDataStore.get(collection)?.get(key) ?? null;
          return { data: doc };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_query': {
        if (this.options.previewMode) {
          const { collection, limit, orderBy } = msg.payload as {
            collection: string;
            filters?: Array<{ field: string; op: string; value: unknown }>;
            orderBy?: { field: string; direction: 'asc' | 'desc' };
            limit?: number;
          };
          const col = this.previewDataStore.get(collection);
          if (!col) return { documents: [], nextCursor: null };
          let docs = [...col.entries()].map(([k, v]) => ({
            key: k,
            data: v,
            createdAt: (v._updatedAt as string) ?? new Date().toISOString(),
            updatedAt: (v._updatedAt as string) ?? new Date().toISOString(),
          }));
          if (orderBy) {
            docs.sort((a, b) => {
              const aVal = a.data[orderBy.field];
              const bVal = b.data[orderBy.field];
              if (aVal === bVal) return 0;
              const cmp = aVal != null && bVal != null && aVal < bVal ? -1 : 1;
              return orderBy.direction === 'desc' ? -cmp : cmp;
            });
          }
          if (limit) docs = docs.slice(0, limit);
          return { documents: docs, nextCursor: null };
        }
        return this.proxyToBackend(msg);
      }

      case 'data_delete': {
        if (this.options.previewMode) {
          const { collection, key } = msg.payload as { collection: string; key: string };
          this.previewDataStore.get(collection)?.delete(key);
          return { success: true };
        }
        return this.proxyToBackend(msg);
      }

      default:
        throw new Error(`Unknown bridge call type: ${msg.type}`);
    }
  }

  /**
   * Handle streaming entity invoke in preview mode via SSE.
   * Sends stream_token messages to the plugin as tokens arrive,
   * then returns the final result for the normal response message.
   */
  private async handlePreviewStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/arches/plugin-builder/sessions/${this.options.archId.replace('preview_', '')}/ai-invoke`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ input: payload.input, entityId: payload.entityId, stream: true }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    // If SSE streaming, consume the stream
    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.text) {
              fullOutput += parsed.text;
              // Forward token to plugin iframe
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch {
            // Skip malformed SSE data
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-streaming fallback
    return response.json();
  }

  /**
   * Handle streaming entity invoke for published plugins via the bridge endpoint.
   */
  private async handlePublishedStreamingInvoke(
    messageId: string,
    payload: { entityId: string; input: string },
  ): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/plugins/bridge/${this.options.archId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'entity_invoke',
          payload: { ...payload, stream: true },
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'AI invocation failed' }));
      throw new Error(err.error?.message ?? 'AI invocation failed');
    }

    if (response.headers.get('content-type')?.includes('text/event-stream') && response.body) {
      let fullOutput = '';
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6);
          if (data === '[DONE]') continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.text) {
              fullOutput += parsed.text;
              this.options.iframe.contentWindow?.postMessage(
                { type: 'stream_token', messageId, text: parsed.text },
                '*',
              );
            }
            if (parsed.done && parsed.output) {
              fullOutput = parsed.output;
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'AI invocation failed') throw e;
          }
        }
      }

      return { output: fullOutput };
    }

    // Non-SSE fallback
    return response.json();
  }

  private async proxyToBackend(msg: BridgeMessage): Promise<unknown> {
    const token = await this.options.getAuthToken();
    const response = await fetch(
      `${this.options.bridgeApiUrl}/plugins/bridge/${this.options.archId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: msg.type,
          payload: msg.payload,
        }),
      },
    );

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Bridge call failed' }));
      throw new Error(err.error?.message ?? 'Bridge call failed');
    }

    return response.json();
  }

  private checkRateLimit(type: string): void {
    // Burst detection first (catches runaway loops in <5s)
    this.checkBurst(type);

    const limit = RATE_LIMITS[type];
    if (!limit) return;

    const now = Date.now();
    const bucket = this.rateLimits.get(type);

    if (!bucket || now - bucket.windowStart > 60_000) {
      this.rateLimits.set(type, { count: 1, windowStart: now });
      return;
    }

    if (bucket.count >= limit.maxPerMinute) {
      throw new Error(`Rate limit exceeded for ${type}: max ${limit.maxPerMinute}/minute`);
    }

    bucket.count++;
  }

  private checkBurst(type: string): void {
    const now = Date.now();
    const bucket = this.burstBuckets.get(type);

    // If currently in cooldown, block silently (onLog already fired on first breach)
    if (bucket?.blockedUntil && now < bucket.blockedUntil) {
      throw new Error(
        `Bridge call "${type}" temporarily blocked — rapid-fire loop detected. Check useEffect dependency arrays.`,
      );
    }

    // Reset expired burst windows
    if (!bucket || now - bucket.windowStart > BURST_WINDOW_MS) {
      this.burstBuckets.set(type, { count: 1, windowStart: now });
      return;
    }

    bucket.count++;

    if (bucket.count > BURST_THRESHOLD) {
      bucket.blockedUntil = now + BURST_COOLDOWN_MS;
      const msg =
        `Runaway loop detected: "${type}" called ${bucket.count} times in ${BURST_WINDOW_MS / 1000}s. ` +
        `Calls blocked for ${BURST_COOLDOWN_MS / 1000}s. Check useEffect dependency arrays.`;
      this.options.onLog?.('error', msg);
      throw new Error(
        `Bridge call "${type}" temporarily blocked — rapid-fire loop detected. Check useEffect dependency arrays.`,
      );
    }
  }

  private sendToPlugin(message: BridgeMessage): void {
    // targetOrigin '*' is required because sandboxed iframes without allow-same-origin
    // have an opaque (null) origin. Security is enforced by checking event.source
    // against iframe.contentWindow on incoming messages (line 113).
    this.options.iframe.contentWindow?.postMessage(message, '*');
  }

  private sendResponse(messageId: string, payload: unknown): void {
    this.sendToPlugin({
      type: 'response' as string,
      messageId,
      payload,
    });
  }

  private sendError(messageId: string, error: string): void {
    // See sendToPlugin() for why '*' is used as targetOrigin.
    this.options.iframe.contentWindow?.postMessage(
      {
        type: 'response',
        messageId,
        payload: null,
        error,
      },
      '*',
    );
  }
}
